import { useState, type ReactNode } from 'react';
import { Container, PageHeader, Section, ActionBar } from '@vnetwork/ui/layout';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';

// IAM entity names allow word chars plus + = , . @ - ; mirror the backend rule
// so the user gets the error before the round-trip.
const NAME_RE = /^[\w+=,.@-]{1,128}$/;

/**
 * Name-only create screen shared by the user and group flows (both take just a
 * name). Validates the IAM name grammar client-side; the caller supplies the
 * mutation via `onSubmit(name)` plus its pending flag and cancel target.
 */
export function IamNameCreateForm({
  title,
  description,
  label,
  placeholder,
  icon,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  title: string;
  description: string;
  label: string;
  placeholder: string;
  icon: ReactNode;
  submitLabel: string;
  pending: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);

  const nameError =
    touched && !NAME_RE.test(name)
      ? 'Use 1–128 chars: letters, digits or + = , . @ - _'
      : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!NAME_RE.test(name)) return;
    onSubmit(name);
  };

  return (
    <Container size="xl">
      <PageHeader icon={icon} title={title} description={description} />
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section title={label} style={{ marginBottom: 0 }}>
          <div style={{ maxWidth: 720 }}>
            <Field label={label} required error={nameError ?? undefined}>
              {(p) => (
                <Input
                  {...p}
                  placeholder={placeholder}
                  value={name}
                  invalid={!!nameError}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
              )}
            </Field>
          </div>
        </Section>

        <ActionBar
          start={
            <Button type="button" variant="secondary" disabled={pending} onClick={onCancel}>
              Cancel
            </Button>
          }
          end={
            <Button type="submit" variant="primary" disabled={pending}>
              {pending ? 'Creating…' : submitLabel}
            </Button>
          }
        />
      </form>
    </Container>
  );
}
